import { timestampToDate } from "@storytell/cp-client";
import type { Message } from "@storytell/cp-client/src/gen/chat/v3/chat_messages_pb";
import { produce } from "solid-js/store";
import type {
  SetStreamingStore,
  StreamingMessageId,
  StreamingOperation,
  StreamingOperationId,
} from "../streamingTypes";

export const OPERATION_INDEX_OFFSET = -1;

export const processOperationStart = (setStore: SetStreamingStore, message: Message) => {
  if (message.data.case !== "messageOperationStatusData") {
    throw new Error(`Expected messageOperationStatusData, got ${message.data.case}`);
  }
  const {
    id,
    index: _index,
    role,
    operationId: _operationId,
    data: { value: data },
  } = message;

  const index = _index + OPERATION_INDEX_OFFSET;
  const operationId = _operationId as StreamingOperationId;
  const messageId = id as StreamingMessageId;

  const streamingOperation: StreamingOperation = {
    index,
    operationId,
    isComplete: false,
    messageId,
    blocks: [],
    name: data.operationName,
    activity: data.activity,
    startedAt: data.startedAt ? timestampToDate(data.startedAt) : undefined,
    role: role,
    status: data.status,
    completedAt: data.completedAt ? timestampToDate(data.completedAt) : undefined,
    task: undefined,
    progressMessage: data.progressMessage || undefined,
  };

  setStore(
    produce((store) => {
      const message = store.messages[messageId];
      if (message && !message.operations.includes(operationId)) {
        message.operations.splice(index, 0, operationId);
      }

      store.operations[operationId] = streamingOperation;
    }),
  );
};

export const processOperationProgress = (setStore: SetStreamingStore, message: Message) => {
  if (message.data.case !== "messageOperationStatusData") {
    throw new Error(`Expected messageOperationStatusData, got ${message.data.case}`);
  }

  const {
    operationId: _operationId,
    data: { value: data },
  } = message;
  const operationId = _operationId as StreamingOperationId;

  setStore(
    produce((store) => {
      const operation = store.operations[operationId];
      if (!operation) {
        throw new Error(`Operation not found for progress: ${operationId}`);
      }

      if (data.operationName !== operation.name) {
        operation.name = data.operationName;
      }
      if (data.activity !== operation.activity) {
        operation.activity = data.activity;
      }
      if (data.progressMessage !== operation.progressMessage) {
        operation.progressMessage = data.progressMessage || undefined;
      }
      if (data.status !== operation.status) {
        operation.status = data.status;
      }
    }),
  );
};

export const processOperationComplete = (setStore: SetStreamingStore, message: Message) => {
  if (message.data.case !== "messageOperationStatusData") {
    throw new Error(`Expected messageOperationStatusData, got ${message.data.case}`);
  }

  const {
    operationId: _operationId,
    data: { value: data },
  } = message;
  const operationId = _operationId as StreamingOperationId;

  setStore(
    produce((store) => {
      const operation = store.operations[operationId];
      if (!operation) {
        throw new Error(`Operation not found for complete: ${operationId}`);
      }

      operation.isComplete = true;
      if (data.operationName !== operation.name) {
        operation.name = data.operationName;
      }
      if (data.activity !== operation.activity) {
        operation.activity = data.activity;
      }
      if (data.startedAt !== operation.startedAt) {
        operation.startedAt = data.startedAt ? timestampToDate(data.startedAt) : undefined;
      }
      if (data.status !== operation.status) {
        operation.status = data.status;
      }
      if (data.completedAt !== operation.completedAt) {
        operation.completedAt = data.completedAt ? timestampToDate(data.completedAt) : undefined;
      }
      if (data.progressMessage !== operation.progressMessage) {
        operation.progressMessage = data.progressMessage || undefined;
      }

      // for (const blockId of operation.blocks) {
      //   const block = store.blocks[blockId];
      //   if (block) {
      //     block.isComplete = true;
      //   }
      // }
    }),
  );
};
